import { useMemo } from 'react';
import { resolveStatus, type PublicUser, type ResolvedStatus } from '@avslack/shared';
import { useActiveHuddles } from '../data/huddles';
import { useMinute } from '../lib/clock';

/**
 * A megjelenitendo statusz.
 *
 * A szamitas SZANDEKOSAN a kliensen tortenik, nem a szerveren: az automatikus
 * statuszok idofuggok ("munkaidon kivul") es allapotfuggok ("huddle-ben"),
 * amiket a kliens ugyis percrol percre lat. Ha a szerver szamolna, minden
 * felhasznalo minden lekerdezesehez ket extra query kellene - vagy elavult
 * ertek jelenne meg.
 *
 * "Percrol percre" viszont csak a {@link useMinute} ota igaz. Elotte az ertek
 * a komponens utolso ujrarajzolasakor fagyott be: aki reggel nyolckor nyitotta
 * meg az alkalmazast, delig "munkaidon kivul" volt a sajat kepernyojen, es
 * csak egy oldal-ujratoltes vette le rola.
 */
export function useResolvedStatus(user: PublicUser | undefined): ResolvedStatus {
  const { data: huddles } = useActiveHuddles();
  const minute = useMinute();

  return useMemo(() => {
    if (!user) return { emoji: null, text: null, auto: null };

    const inHuddle = (huddles ?? []).some((huddle) =>
      huddle.participants.some((participant) => participant.user.id === user.id),
    );

    return resolveStatus({
      manualEmoji: user.statusEmoji,
      manualText: user.statusText,
      manualExpiresAt: user.statusExpiresAt,
      flags: user.autoStatus,
      inHuddle,
      meeting: user.meeting,
      focusUntil: user.focusUntil,
      workingHours: user.workingHours,
      timezone: user.timezone,
      now: new Date(minute),
    });
  }, [user, huddles, minute]);
}

/**
 * Statusz egy sorban.
 *
 * Az automatikus statusz halvanyabb, mint a kezzel beallitott - latszodjon,
 * hogy a rendszer kovetkeztette ki, nem a kollega irta oda.
 */
export function UserStatus({
  user,
  className = '',
  fallback,
}: {
  user: PublicUser | undefined;
  className?: string;
  /** Ha nincs statusz, ez jelenik meg (pl. a beosztas). */
  fallback?: string | null;
}) {
  const status = useResolvedStatus(user);

  if (!status.emoji && !status.text) {
    return fallback ? <span className={className}>{fallback}</span> : null;
  }

  return (
    <span className={`${className} ${status.auto ? 'opacity-75' : ''}`}>
      {status.emoji && (
        <span aria-hidden className="mr-1">
          {status.emoji}
        </span>
      )}
      {status.text}
    </span>
  );
}
